// izipush-crm — textes des écrans CRM, en français (par défaut) et en anglais (navigateur en anglais).
// Le reste du dashboard Novu n'est pas traduit : ce dictionnaire ne couvre que le CRM.

export type CrmLocale = 'fr' | 'en';

function detectLocale(): CrmLocale {
  if (typeof navigator === 'undefined') return 'fr';

  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
}

export const crmLocale: CrmLocale = detectLocale();

const fr = {
  // Commun
  'common.cancel': 'Annuler',
  'common.save': 'Enregistrer',
  'common.delete': 'Supprimer',
  'common.edit': 'Modifier',
  'common.back': 'Retour',
  'common.continue': 'Continuer',
  'common.previous': 'Précédent',
  'common.actions': 'Actions',
  'common.optional': 'facultatif',
  'common.loading': 'Chargement…',
  'common.moreActions': "Plus d'actions",
  'common.openDetails': 'Ouvrir le détail',

  // Menu
  'nav.segments': 'Segments',
  'nav.campaigns': 'Campagnes',
  'nav.templates': 'Templates email',
  'nav.reports': 'Rapports',
  'nav.monitoring': 'Suivi',

  // Pluriels
  'clients.one': '{count} client',
  'clients.other': '{count} clients',
  'runs.one': '{count} exécution',
  'runs.other': '{count} exécutions',
  'errors.one': '{count} erreur',
  'errors.other': '{count} erreurs',
  'steps.one': '{count} étape email',
  'steps.other': '{count} étapes email',

  // Statuts
  'segment.status.ready': 'Prêt',
  'segment.status.freezing': 'Figeage en cours',
  'segment.status.deleting': 'Suppression…',
  'segment.status.failed': 'Figeage en échec',
  'campaign.status.draft': 'Brouillon',
  'campaign.status.active': 'Active',
  'campaign.status.paused': 'En pause',
  'campaign.status.completed': 'Terminée',
  'run.status.resolving': 'Calcul de la liste',
  'run.status.triggering': 'Envoi en cours',
  'run.status.triggered': 'Envoyée',
  'run.status.failed': 'Échec',

  // Opérateurs
  'op.eq': 'est',
  'op.ne': "n'est pas",
  'op.in': "est l'un de",
  'op.nin': "n'est aucun de",
  'op.gt': 'est supérieur à',
  'op.gte': 'est au moins',
  'op.lt': 'est inférieur à',
  'op.lte': 'est au plus',
  'op.exists': 'est renseigné',
  'op.not_exists': "n'est pas renseigné",
  'op.within_last_days': 'date de moins de',
  'op.more_than_days_ago': 'date de plus de',
  'op.has': 'utilise',
  'op.has_not': "n'utilise pas",
  'op.has_all': 'utilise tous',
  'op.has_any': "utilise l'un de",
  'op.days': 'jours',
  'activity.op.gt': 'supérieur à',
  'activity.op.gte': 'au moins',
  'activity.op.lt': 'inférieur à',
  'activity.op.lte': 'au plus',
  'activity.op.eq': 'égal à',
  'segEditor.activityZero':
    'Un client sans aucune activité sur la période compte pour 0 : « égal à 0 » cible les inactifs.',

  // Champs du profil (les autres gardent le libellé de l'API)
  'field.country_code': 'Pays',
  'field.kyc_status': 'Statut KYC',
  'field.account_created_at': "Date d'inscription",
  'field.kyc_validated_at': 'Date de validation KYC',
  'field.first_tx_at': 'Première transaction',
  'field.last_tx_at': 'Dernière transaction',
  'field.last_login_at': 'Dernière connexion',
  'field.lifetime_tx': 'Transactions (depuis toujours)',
  'field.lifetime_vol_usd': 'Volume en USD (depuis toujours)',
  'field.marketing_optin': 'Accepte le marketing',
  'field.email': 'Email',
  'field.locale': 'Langue',
  'metric.tx': 'Transactions réussies',
  'metric.volUsd': 'Volume (USD)',
  'metric.txFailed': 'Transactions échouées',

  // Événements
  'event.account.registered': 'Inscription',
  'event.account.profile_updated': 'Profil modifié',
  'event.account.email_updated': 'Email modifié',
  'event.account.logged_in': 'Connexion',
  'event.kyc.validated': 'KYC validé',
  'event.transaction.completed': 'Transaction',
  'event.product.activated': 'Produit activé',

  // Canaux
  'channel.email': 'Email',
  'channel.push': 'Push',
  'channel.in_app': 'In-app',
  'channel.sms': 'SMS',
  'channel.chat': 'Chat',

  // Planification
  'schedule.mode.immediate': 'Maintenant',
  'schedule.mode.scheduled': 'À une date',
  'schedule.mode.recurring': 'Régulièrement',
  'schedule.mode.on_event': 'Sur un événement',
  'schedule.describe.immediate': "Une fois, dès l'activation",
  'schedule.describe.scheduled': 'Le {date}',
  'schedule.describe.onEvent': 'À chaque « {event} »',
  'schedule.describe.daily': 'Chaque jour à {time}',
  'schedule.describe.weekly': 'Chaque {days} à {time}',
  'schedule.describe.monthly': 'Le {day} de chaque mois à {time}',
  'schedule.describe.custom': 'Planification personnalisée ({cron})',
  'schedule.describe.tz': '{text} ({tz})',
  'schedule.and': 'et',
  'weekday.1': 'lundi',
  'weekday.2': 'mardi',
  'weekday.3': 'mercredi',
  'weekday.4': 'jeudi',
  'weekday.5': 'vendredi',
  'weekday.6': 'samedi',
  'weekday.7': 'dimanche',
  'weekday.short.1': 'Lun',
  'weekday.short.2': 'Mar',
  'weekday.short.3': 'Mer',
  'weekday.short.4': 'Jeu',
  'weekday.short.5': 'Ven',
  'weekday.short.6': 'Sam',
  'weekday.short.7': 'Dim',
  'freq.daily': 'Chaque jour',
  'freq.weekly': 'Chaque semaine',
  'freq.monthly': 'Chaque mois',
  'freq.custom': 'Personnalisée',
  'tz.Africa/Abidjan': 'Afrique de l’Ouest, GMT (Abidjan, Dakar, Lomé, Bamako)',
  'tz.Africa/Lagos': 'Afrique de l’Ouest, GMT+1 (Cotonou, Niamey, Lagos)',
  'tz.Africa/Douala': 'Afrique centrale, GMT+1 (Douala, Libreville)',
  'tz.Africa/Kinshasa': 'Kinshasa, GMT+1',
  'tz.Africa/Lubumbashi': 'Lubumbashi, GMT+2',
  'tz.Europe/Paris': 'Paris',
  'tz.UTC': 'UTC',

  // Campagnes : liste
  'campaigns.description':
    'Envoyez un workflow à un segment : maintenant, à une date, régulièrement, ou quand un client fait une action.',
  'campaigns.new': 'Nouvelle campagne',
  'campaigns.blank.title': 'Aucune campagne pour l’instant',
  'campaigns.blank.text':
    'Une campagne associe un segment (qui), un workflow (quoi) et un moment (quand). Elle reste en brouillon tant que vous ne l’activez pas.',
  'campaigns.col.name': 'Campagne',
  'campaigns.col.schedule': 'Envoi',
  'campaigns.col.status': 'Statut',
  'campaigns.col.next': 'Prochain envoi',
  'campaigns.col.last': 'Dernier envoi',
  'campaigns.action.view': 'Voir les résultats',
  'campaigns.action.activate': 'Activer',
  'campaigns.action.pause': 'Mettre en pause',
  'campaigns.toast.activated': 'Campagne activée',
  'campaigns.toast.paused': 'Campagne mise en pause',
  'campaigns.toast.activateFailed': 'Campagne non activée',
  'campaigns.toast.pauseFailed': 'Pause impossible',
  'campaigns.toast.deleted': 'Campagne supprimée',
  'campaigns.toast.deleteFailed': 'Campagne non supprimée',
  'campaigns.delete.title': 'Supprimer la campagne ?',
  'campaigns.delete.text':
    '« {name} » et son historique ne seront plus visibles. Les messages déjà envoyés ne sont pas concernés.',
  'campaigns.pauseToEdit': 'Mettez la campagne en pause pour la modifier.',
  'campaigns.pauseToDelete': 'Mettez la campagne en pause pour la supprimer.',
  'campaigns.activate.title': 'Activer la campagne ?',
  'campaigns.activate.now': 'Les messages partent tout de suite vers les clients de « {segment} ».',
  'campaigns.activate.later': 'La campagne partira selon sa planification : {when}.',
  'campaigns.activate.confirm': 'Activer',

  // Campagnes : assistant
  'editor.newTitle': 'Nouvelle campagne',
  'editor.editTitle': 'Modifier « {name} »',
  'editor.notFound': 'Campagne introuvable.',
  'editor.locked': 'Cette campagne est active. Mettez-la en pause pour la modifier.',
  'step.audience': 'Audience',
  'step.audience.hint': 'Qui la reçoit',
  'step.message': 'Message',
  'step.message.hint': 'Le workflow envoyé',
  'step.schedule': 'Envoi',
  'step.schedule.hint': 'Quand l’envoyer',
  'step.review': 'Vérification',
  'step.review.hint': 'Relire avant d’enregistrer',
  'step.progress': 'Étape {current} sur {total}',
  'editor.name.label': 'Nom de la campagne',
  'editor.name.placeholder': 'Relance des inactifs 30 jours',
  'editor.name.hint': 'Visible seulement dans le dashboard.',
  'editor.segment.label': 'Segment',
  'editor.segment.placeholder': 'Choisir un segment',
  'editor.segment.create': 'Créer un segment',
  'editor.segment.empty': 'Aucun segment. Créez-en un pour choisir votre audience.',
  'editor.segment.dynamic': 'Liste recalculée à chaque envoi',
  'editor.segment.frozen': 'Liste figée le {date}',
  'editor.segment.today': "{clients} aujourd'hui",
  'editor.segment.counting': 'Calcul du nombre de clients…',
  'editor.segment.freezing': 'Figeage en cours : la campagne pourra partir une fois la liste prête.',
  'editor.segment.exclusions':
    'Les clients qui refusent le marketing ou dont le compte est supprimé sont écartés au moment de l’envoi.',
  'editor.workflow.label': 'Workflow',
  'editor.workflow.placeholder': 'Choisir un workflow',
  'editor.workflow.hint': 'Le contenu (push, email…) se règle dans le workflow.',
  'editor.workflow.empty': 'Aucun workflow dans cet environnement. Créez-le dans Development, puis publiez-le.',
  'editor.workflow.open': 'Ouvrir le workflow',
  'editor.workflow.missing': 'Ce workflow n’existe plus dans cet environnement ({key}).',
  'editor.payload.title': 'Données personnalisées',
  'editor.payload.hint': 'Des valeurs utilisables dans le workflow, par exemple {{payload.offre}}.',
  'editor.payload.key': 'Nom',
  'editor.payload.value': 'Valeur',
  'editor.payload.add': 'Ajouter une donnée',
  'editor.payload.remove': 'Retirer cette donnée',
  'editor.schedule.label': 'Quand envoyer ?',
  'editor.schedule.immediate.hint': 'La campagne part une seule fois, dès que vous l’activez.',
  'editor.schedule.at.label': 'Date et heure',
  'editor.schedule.at.hint': 'À l’heure de votre ordinateur.',
  'editor.schedule.frequency': 'Fréquence',
  'editor.schedule.days': 'Jours',
  'editor.schedule.dayOfMonth': 'Jour du mois',
  'editor.schedule.dayOfMonth.hint': 'Du 1er au 28, pour que la campagne parte aussi en février.',
  'editor.schedule.time': 'Heure',
  'editor.schedule.timezone': 'Fuseau horaire',
  'editor.schedule.cron': 'Expression cron',
  'editor.schedule.cron.hint': 'Pour une planification qu’on ne peut pas décrire autrement.',
  'editor.schedule.event.label': 'Événement',
  'editor.schedule.event.placeholder': 'Choisir un événement',
  'editor.schedule.event.hint':
    'Le client qui fait l’action reçoit la campagne s’il fait partie du segment (événements de moins de 24 h).',
  'editor.review.who': 'Qui',
  'editor.review.what': 'Quoi',
  'editor.review.when': 'Quand',
  'editor.review.data': 'Données',
  'editor.review.noData': 'Aucune',
  'editor.review.safety': 'Rien n’est envoyé tant que la campagne n’est pas activée.',
  'editor.review.saveDraft': 'Enregistrer en brouillon',
  'editor.review.saveActivate': 'Enregistrer et activer',
  'editor.review.saveChanges': 'Enregistrer les modifications',
  'editor.review.change': 'Modifier',
  'editor.footer.hint': 'Enregistrée en brouillon : rien n’est envoyé avant l’activation.',
  'editor.validation.name': 'Donnez un nom à la campagne.',
  'editor.validation.segment': 'Choisissez un segment.',
  'editor.validation.workflow': 'Choisissez un workflow.',
  'editor.validation.at': 'Choisissez une date à venir.',
  'editor.validation.days': 'Choisissez au moins un jour.',
  'editor.validation.cron': 'L’expression doit compter 5 éléments, par exemple « 0 9 * * 1 ».',
  'editor.validation.event': 'Choisissez un événement.',
  'editor.toast.draft': 'Campagne enregistrée en brouillon',
  'editor.toast.activated': 'Campagne enregistrée et activée',
  'editor.toast.updated': 'Campagne mise à jour',
  'editor.toast.failed': 'Campagne non enregistrée',
  'editor.toast.activateFailed': 'Campagne enregistrée, mais non activée',
  'editor.unsaved': 'Les modifications de la campagne seront perdues.',

  // Campagne : détail
  'detail.results': 'Résultats',
  'detail.runs': 'Exécutions',
  'detail.runs.empty': 'Aucune exécution pour l’instant. Elles apparaissent ici dès le premier envoi.',
  'detail.onEvent': 'Envois sur événement, 30 derniers jours',
  'detail.row.status': 'Statut',
  'detail.row.audience': 'Audience',
  'detail.row.workflow': 'Workflow',
  'detail.row.schedule': 'Envoi',
  'detail.row.next': 'Prochain envoi',
  'detail.row.last': 'Dernier envoi',
  'detail.row.created': 'Créée le',
  'detail.error': 'Dernière erreur',
  'stat.sent': 'Envoyés',
  'stat.errors': 'Erreurs',
  'stat.skipped': 'Écartés',
  'stat.opened': 'Ouvertures',
  'stat.clicked': 'Clics',
  'stat.audience': 'Visés',
  'stat.excluded': 'Exclus',
  'stat.openRate': 'Taux d’ouverture',
  'stat.clickRate': 'Taux de clic',
  'stat.openHint':
    'Push : notification touchée dans l’app. Email : ouverture signalée par le fournisseur, surestimée par certaines messageries.',
  'stat.skippedHint': 'Messages non envoyés : adresse exclue (bounce, plainte) ou préférences du client.',
  'stat.excludedHint': 'Clients du segment écartés à l’envoi : refus du marketing ou compte supprimé.',
  'run.col.date': 'Date',
  'run.col.detail': 'Détail',

  // Segments : liste
  'segments.description': 'Des groupes de clients définis par leur profil et leur activité.',
  'segments.new': 'Nouveau segment',
  'segments.blank.title': 'Aucun segment pour l’instant',
  'segments.blank.text':
    'Un segment réunit les clients qui remplissent des critères : pays, KYC, volume de transactions… Les campagnes s’en servent pour choisir leur audience.',
  'segments.col.name': 'Segment',
  'segments.col.type': 'Liste',
  'segments.col.count': 'Clients',
  'segments.col.status': 'Statut',
  'segments.col.created': 'Créé le',
  'segments.type.dynamic': 'Recalculée à chaque envoi',
  'segments.type.frozen': 'Figée le {date}',
  'segments.count.dynamic': 'Calculé à l’envoi',
  'segments.action.retry': 'Relancer le figeage',
  'segments.toast.retried': 'Figeage relancé',
  'segments.toast.retryFailed': 'Figeage non relancé',
  'segments.toast.deleted': 'Segment supprimé',
  'segments.toast.deleteFailed': 'Segment non supprimé',
  'segments.delete.title': 'Supprimer le segment ?',
  'segments.delete.text': '« {name} » sera supprimé. Un segment utilisé par une campagne ne peut pas l’être.',

  // Segment : création
  'segEditor.title': 'Nouveau segment',
  'segEditor.name.label': 'Nom du segment',
  'segEditor.name.placeholder': 'Actifs crypto Côte d’Ivoire',
  'segEditor.description.label': 'Description',
  'segEditor.description.placeholder': 'À quoi sert ce segment ?',
  'segEditor.criteria.title': 'Critères',
  'segEditor.criteria.lead': 'Les clients qui remplissent',
  'segEditor.combinator.and': 'tous les critères',
  'segEditor.combinator.or': 'au moins un critère',
  'segEditor.criteria.empty': 'Sans critère, le segment contient tous les clients.',
  'segEditor.addProfile': 'Critère de profil',
  'segEditor.addActivity': 'Critère d’activité',
  'segEditor.activityAll': 'Avec un critère d’activité, tous les critères doivent être remplis.',
  'segEditor.connector.and': 'et',
  'segEditor.connector.or': 'ou',
  'segEditor.activity.over': 'sur les',
  'segEditor.activity.days': 'derniers jours',
  'segEditor.activity.product': 'produit',
  'segEditor.activity.allProducts': 'tous',
  'segEditor.value.choose': 'Choisir',
  'segEditor.value.yes': 'Oui',
  'segEditor.value.no': 'Non',
  'segEditor.value.list': 'Choisir une ou plusieurs valeurs',
  'segEditor.value.listText': 'valeur1, valeur2',
  'segEditor.removeCriterion': 'Retirer ce critère',
  'segEditor.subgroup': 'Groupe de critères créé par l’API, non modifiable ici',
  'segEditor.list.title': 'Liste de clients',
  'segEditor.list.dynamic.title': 'Recalculée à chaque envoi',
  'segEditor.list.dynamic.text': 'Les nouveaux clients qui remplissent les critères sont inclus automatiquement.',
  'segEditor.list.frozen.title': 'Figée à aujourd’hui',
  'segEditor.list.frozen.text':
    'La liste est photographiée maintenant et ne changera plus. Pratique pour une opération ponctuelle.',
  'segEditor.preview.title': 'Clients correspondants',
  'segEditor.preview.hint': 'Calculé sur les profils à jour, avant les exclusions de l’envoi.',
  'segEditor.preview.incomplete': 'Complétez les critères pour voir le nombre de clients.',
  'segEditor.create': 'Créer le segment',
  'segEditor.toast.created': 'Segment créé',
  'segEditor.toast.createdFrozen': 'Segment créé : la liste est en cours de figeage',
  'segEditor.toast.failed': 'Segment non créé',
  'segEditor.validation.name': 'Donnez un nom au segment.',
  'segEditor.unsaved': 'Le segment n’est pas encore créé : vos critères seront perdus.',

  // Templates
  'templates.description':
    'Des emails réutilisables. Choisissez-en un dans une étape email : chaque modification du template y est recopiée.',
  'templates.new': 'Nouveau template',
  'templates.blank.title': 'Aucun template pour l’instant',
  'templates.blank.text':
    'Concevez vos emails une fois dans l’éditeur visuel, puis réutilisez-les dans vos workflows. Créez-les dans l’environnement où vous éditez vos workflows.',
  'templates.col.name': 'Template',
  'templates.col.subject': 'Objet',
  'templates.col.version': 'Version',
  'templates.col.updated': 'Modifié le',
  'templates.toast.deleted': 'Template supprimé',
  'templates.toast.deleteFailed': 'Template non supprimé',
  'templates.delete.title': 'Supprimer le template ?',
  'templates.delete.text': '« {name} » sera supprimé. Un template utilisé par une étape email ne peut pas l’être.',
  'templateEditor.newTitle': 'Nouveau template',
  'templateEditor.name': 'Nom',
  'templateEditor.namePlaceholder': 'Relance KYC',
  'templateEditor.subject': 'Objet proposé aux étapes email',
  'templateEditor.subjectPlaceholder': '{{subscriber.firstName}}, votre KYC vous attend',
  'templateEditor.usedBy': 'Utilisé par {steps} : elles seront mises à jour à l’enregistrement.',
  'templateEditor.toast.saved': 'Template enregistré',
  'templateEditor.toast.propagated': 'Template enregistré et recopié dans {steps}',
  'templateEditor.toast.failed': 'Template non enregistré',
  'templateEditor.renderFailed': 'Rendu HTML impossible',
  'mergeTag.group.client': 'Client',
  'mergeTag.group.marketing': 'Marketing',
  'mergeTag.group.campaign': 'Campagne',
  'mergeTag.firstName': 'Prénom',
  'mergeTag.lastName': 'Nom',
  'mergeTag.email': 'Email',
  'mergeTag.country': 'Pays',
  'mergeTag.unsubscribe': 'Lien de désinscription',
  'mergeTag.campaignData': 'Donnée de la campagne',
  'templateSelect.placeholder': 'Template CRM',
  'templateSelect.none': 'Sans template CRM',
  'templateSelect.failed': 'Template non appliqué',

  // Rapports
  'reports.description': 'Résultats des campagnes sur la période : envois planifiés et envois sur événement.',
  'reports.period': '{days} j',
  'reports.period.label': 'Période',
  'reports.kpi.campaigns': 'Campagnes',
  'reports.kpi.audience': 'Clients visés',
  'reports.kpi.audienceHint': 'par les envois planifiés',
  'reports.kpi.sent': 'Messages envoyés',
  'reports.byChannel': 'Par canal',
  'reports.byCampaign': 'Par campagne',
  'reports.col.channel': 'Canal',
  'reports.col.type': 'Type',
  'reports.col.runs': 'Exécutions',
  'reports.blank.title': 'Aucun envoi sur la période',
  'reports.blank.text': 'Les résultats apparaissent ici après le premier envoi d’une campagne.',
  'reports.truncated': 'Plus de 1 000 exécutions : seules les plus récentes sont comptées.',

  // Suivi
  'monitoring.title': 'Suivi',
  'monitoring.tab.ingestion': 'Réception des événements',
  'monitoring.tab.email': 'Envois email',
  'monitoring.service.title': 'Service de réception',
  'monitoring.service.never': 'Le service de réception n’a encore jamais signalé son état. Vérifiez qu’il est démarré.',
  'monitoring.service.online': 'En ligne',
  'monitoring.service.stale': 'Sans nouvelles',
  'monitoring.rabbit.connected': 'File Izichange connectée',
  'monitoring.rabbit.disconnected': 'File Izichange déconnectée',
  'monitoring.service.meta': 'Dernier signal {date} · démarré {started} · version {version}',
  'monitoring.stat.waiting': 'Événements en attente',
  'monitoring.stat.rejected': 'Événements rejetés',
  'monitoring.stat.rejectedHint': 'à examiner',
  'monitoring.stat.lastEvent': 'Dernier événement reçu',
  'monitoring.stat.profiles': 'Mise à jour des profils',
  'monitoring.stat.campaigns': 'Travaux de campagne',
  'monitoring.queue': '{waiting} en attente · {active} en cours · {failed} en échec',
  'monitoring.events.title': 'Événements reçus d’Izichange, 14 derniers jours',
  'monitoring.events.empty': 'Aucun événement reçu sur la période.',
  'monitoring.col.event': 'Événement',
  'outcome.accepted': 'Acceptés',
  'outcome.duplicate': 'Doublons',
  'outcome.invalid': 'Rejetés',
  'outcome.ignored': 'Ignorés',
  'monitoring.rejected.title': 'Événements rejetés',
  'monitoring.rejected.text':
    'Format ou données invalides, gardés 30 jours. Une fois la cause corrigée, renvoyez-les dans la file.',
  'monitoring.rejected.count': 'Nombre à renvoyer',
  'monitoring.rejected.replay': 'Renvoyer',
  'monitoring.rejected.empty': 'Aucun événement rejeté ces 30 derniers jours.',
  'monitoring.col.received': 'Reçu le',
  'monitoring.col.reason': 'Motif',
  'monitoring.col.content': 'Contenu',
  'monitoring.replay.toast': 'Renvoi demandé : il est traité dans les 30 secondes',
  'monitoring.replay.failed': 'Renvoi non demandé',
  'monitoring.commands.title': 'Derniers renvois',
  'monitoring.command.line': '{date}, jusqu’à {limit} événements',
  'command.status.pending': 'En attente',
  'command.status.running': 'En cours',
  'command.status.done': 'Terminé',
  'command.status.failed': 'Échec',

  // Envois email (Suivi) et réglages dans la fiche d'intégration
  'email.providers.title': 'Fournisseurs des campagnes',
  'email.providers.text':
    'Les emails de campagne sont répartis entre les fournisseurs activés pour les campagnes, par ordre de priorité. Les limites se règlent dans la fiche de chaque intégration email.',
  'email.providers.empty':
    'Aucun fournisseur n’est utilisé pour les campagnes : c’est le fournisseur principal de Novu qui envoie.',
  'email.providers.configure': 'Régler',
  'email.col.provider': 'Fournisseur',
  'email.col.priority': 'Priorité',
  'email.col.minute': 'Cette minute',
  'email.col.hour': 'Cette heure',
  'email.col.day': "Aujourd'hui",
  'email.usage.title': 'Envois des 14 derniers jours',
  'email.usage.empty': 'Aucun envoi sur la période.',
  'email.usage.sent': 'Envoyés',
  'email.usage.failed': 'Échecs',
  'email.usage.bounced': 'Bounces',
  'email.usage.complaints': 'Plaintes',
  'email.usage.postponed': 'Repoussés',
  'email.usage.postponedHint': 'Envois mis en attente parce que tous les fournisseurs étaient à leur limite.',
  'email.usage.routing': 'Répartition (tous pleins)',
  'sending.title': 'Envoi des campagnes',
  'sending.enabled': 'Utiliser pour les campagnes',
  'sending.enabled.hint':
    'Les emails de campagne sont répartis entre les fournisseurs activés ici, par ordre de priorité. Les emails transactionnels ne sont pas concernés.',
  'sending.order': 'Priorité',
  'sending.order.hint': '1 = essayé en premier.',
  'sending.limits': 'Limites d’envoi',
  'sending.limits.hint':
    'Vide = pas de limite. Quand une limite est atteinte, le fournisseur suivant prend le relais ; si tous sont pleins, l’envoi attend.',
  'sending.perMinute': 'Par minute',
  'sending.perHour': 'Par heure',
  'sending.perDay': 'Par jour',
  'sending.noLimit': 'Illimité',
  'sending.current': 'En ce moment : {minute} cette minute · {hour} cette heure · {day} aujourd’hui',
  'sending.save': 'Enregistrer les réglages d’envoi',
  'sending.toast.saved': 'Réglages d’envoi enregistrés (appliqués sous 30 secondes)',
  'sending.toast.failed': 'Réglages d’envoi non enregistrés',
  'sending.createHint': 'Enregistrés en même temps que l’intégration.',

  // Équipe (auto-hébergé)
  'team.title': 'Membres de l’équipe',
  'team.description':
    'Chaque membre a accès à toute l’organisation « {org} », en Development comme en Production. Cette édition ne gère pas de rôles : tous les membres ont les mêmes droits.',
  'team.invite.label': 'Inviter un collègue',
  'team.invite.placeholder': 'prenom.nom@izichange.com',
  'team.invite.submit': 'Créer l’invitation',
  'team.invite.done': 'Invitation créée pour {email}. Envoyez-lui ce lien :',
  'team.invite.linkHint':
    'Aucun email n’est envoyé automatiquement. Le lien ne sert qu’une fois, et seulement pour cette adresse.',
  'team.copy': 'Copier le lien',
  'team.copied': 'Lien copié',
  'team.col.member': 'Membre',
  'team.col.status': 'Statut',
  'team.col.since': 'Depuis',
  'team.status.active': 'Actif',
  'team.status.invited': 'Invitation en attente',
  'team.you': 'vous',
  'team.empty': 'Vous êtes seul dans cette équipe pour l’instant : invitez vos collègues ci-dessus.',
  'team.action.newLink': 'Générer un nouveau lien',
  'team.action.cancelInvite': 'Annuler l’invitation',
  'team.action.remove': 'Retirer de l’équipe',
  'team.remove.title': 'Retirer {name} ?',
  'team.remove.text': 'Cette personne n’aura plus accès à « {org} ». Son compte n’est pas supprimé.',
  'team.cancel.title': 'Annuler l’invitation ?',
  'team.cancel.text': 'Le lien envoyé à {email} ne fonctionnera plus.',
  'team.toast.removed': 'Membre retiré',
  'team.toast.cancelled': 'Invitation annulée',
  'team.toast.newLink': 'Nouveau lien généré : l’ancien ne fonctionne plus',
  'team.toast.failed': 'Action impossible',
  'invite.pageTitle': 'Rejoindre une équipe',
  'invite.loading': 'Vérification de l’invitation…',
  'invite.invalid.title': 'Invitation invalide',
  'invite.invalid.text':
    'Ce lien a déjà été utilisé ou a été remplacé. Demandez un nouveau lien à la personne qui vous a invité.',
  'invite.goToSignIn': 'Aller à la connexion',
  'invite.title': 'Rejoindre « {org} »',
  'invite.text': '{inviter} vous invite à rejoindre l’équipe « {org} » sur izipush.',
  'invite.signedIn': 'Vous êtes déjà connecté : rejoignez l’équipe avec ce compte.',
  'invite.join': 'Rejoindre l’équipe',
  'invite.tab.register': 'Créer mon compte',
  'invite.tab.login': 'J’ai déjà un compte',
  'invite.firstName': 'Prénom',
  'invite.lastName': 'Nom',
  'invite.firstNameRequired': 'Indiquez votre prénom.',
  'invite.email': 'Email',
  'invite.emailHint': 'L’invitation est liée à cette adresse.',
  'invite.password': 'Mot de passe',
  'invite.passwordHint':
    '8 caractères minimum, avec une majuscule, une minuscule, un chiffre et un caractère spécial (#?!@$%^&*()-).',
  'invite.passwordInvalid': 'Le mot de passe ne respecte pas les règles indiquées.',
  'invite.submitRegister': 'Créer mon compte et rejoindre',
  'invite.submitLogin': 'Me connecter et rejoindre',
  'org.switch': 'Changer d’organisation',
  'org.switchFailed': 'Changement d’organisation impossible',

  'nav.team': 'Équipe',
  'templateNew.title': 'Nouveau template',
  'templateNew.intro':
    'Donnez un nom et une description à votre email. Vous le concevrez ensuite dans l’éditeur visuel, en plein écran.',
  'templateNew.step': 'Étape {current} sur 2',
  'templateNew.step1': 'Informations',
  'templateNew.step2': 'Conception',
  'templateNew.descriptionPlaceholder': 'À quoi sert cet email ? Pour quelle campagne ?',
  'templateNew.subjectHint': 'Proposé aux étapes email qui utilisent ce template. Modifiable ensuite.',
  'templateNew.continue': 'Continuer vers l’éditeur',
  'templateNew.nameRequired': 'Donnez un nom au template.',
  'templateNew.toast.failed': 'Template non créé',
  'templateEditor.showMenu': 'Afficher le menu',
  'templateEditor.hideMenu': 'Masquer le menu',
  'templateEditor.details': 'Détails',
  'templateEditor.detailsTitle': 'Détails du template',
  'templateEditor.detailsText': 'Le nom et la description servent à retrouver le template ; ils ne sont pas envoyés.',
  'templateEditor.toast.detailsSaved': 'Détails enregistrés',
  'templateEditor.notFound': 'Template introuvable.',
  'templateSelect.empty': 'Ce template n’a pas encore de contenu : concevez-le d’abord dans l’éditeur.',

  'funnel.title': 'Parcours des messages',
  'funnel.caption': 'Chaque pourcentage se rapporte à l’étape précédente.',
  'funnel.targeted': 'Visés',
  'funnel.sent': 'Envoyés',
  'funnel.opened': 'Ouverts',
  'funnel.clicked': 'Cliqués',
  'funnel.ofPrevious': 'Part des {step}',
  'funnel.excluded': 'Exclus au lancement',
  'funnel.skipped': 'Ignorés',
  'trend.title': 'Évolution par exécution',
  'trend.aside': '{count} dernières exécutions',
  'trend.empty': 'La tendance apparaît à partir de deux exécutions envoyées.',
  'detail.runs.open': 'Voir les destinataires de l’exécution du {date}',
  'detail.onEventLink': 'Voir les envois des {days} derniers jours',
  'runPage.title': 'Exécution du {date}',
  'runPage.eventsTitle': 'Envois sur événement',
  'runPage.eventsDescription': 'Messages envoyés sur les {days} derniers jours.',
  'runPage.channels': 'Envoyés par canal',
  'runPage.recipients': 'Destinataires',
  'runPage.recipientsHint': 'Une ligne par message : un client reçoit un message par canal du workflow.',
  'runPage.filter.all': 'Tous',
  'runPage.filter.sent': 'Envoyés',
  'runPage.filter.unopened': 'Non ouverts',
  'runPage.filter.opened': 'Ouverts',
  'runPage.filter.clicked': 'Cliqués',
  'runPage.filter.error': 'Erreurs',
  'runPage.filter.skipped': 'Ignorés',
  'runPage.search': 'ID client ou email exact',
  'runPage.col.client': 'Client',
  'runPage.col.channel': 'Canal',
  'runPage.col.status': 'Statut',
  'runPage.col.sentAt': 'Envoyé le',
  'runPage.col.openedAt': 'Ouvert',
  'runPage.col.clickedAt': 'Cliqué le',
  'runPage.status.sent': 'Envoyé',
  'runPage.status.error': 'Erreur',
  'runPage.status.skipped': 'Ignoré',
  'runPage.seen': 'Vu',
  'runPage.read': 'Lu',
  'runPage.deletedClient': 'Client supprimé',
  'runPage.empty': 'Aucun message ne correspond à ce filtre.',
  'runPage.emptySearch': 'Aucun message pour ce client. La recherche porte sur l’ID exact ou l’email exact.',
  'runPage.notTriggered': 'Cette exécution n’a pas encore été envoyée.',
  'runPage.loadFailed': 'Impossible de charger les destinataires. Réessayez dans un instant.',
  'runPage.export': 'Exporter en CSV',
  'runPage.exporting': 'Export… {count}',
  'runPage.exportDone': 'Export prêt : {count} lignes',
  'runPage.exportCapped': 'Export limité aux {count} premières lignes',
  'runPage.exportFailed': 'Export impossible',

  // izipush-crm — catalogue produits
  'nav.products': 'Produits',
  'products.description':
    'Les produits d’Izichange. Un client est lié à un produit dès qu’il a tenté de l’utiliser : c’est ce lien qui rend le cross-selling possible.',
  'products.new': 'Nouveau produit',
  'products.blank.title': 'Aucun produit déclaré',
  'products.blank.text':
    'Déclarez vos produits avec l’identifiant qu’Izichange envoie dans ses événements. Les transactions s’y rattachent d’elles-mêmes.',
  'products.col.name': 'Produit',
  'products.col.id': 'Identifiant',
  'products.col.subscribers': 'Clients',
  'products.col.tx': 'Transactions',
  'products.col.volume': 'Volume USD',
  'products.col.status': 'État',
  'products.status.active': 'Actif',
  'products.status.archived': 'Archivé',
  'products.status.unnamed': 'À nommer',
  'products.unnamed.hint': 'Créé automatiquement à la réception d’un identifiant inconnu : donnez-lui un nom.',
  'products.unknown.hint':
    'Bac des transactions reçues sans identifiant de produit. Renommez-le le jour où vous savez ce qu’il recouvre ; il ne peut pas être supprimé.',
  'products.archive': 'Archiver',
  'products.unarchive': 'Réactiver',
  'products.delete.title': 'Supprimer ce produit ?',
  'products.delete.text':
    'Le produit « {name} » sera retiré du catalogue. Son activité et ses campagnes l’en empêchent.',
  'products.toast.created': 'Produit créé',
  'products.toast.updated': 'Produit enregistré',
  'products.toast.deleted': 'Produit supprimé',
  'products.toast.createFailed': 'Création impossible',
  'products.toast.updateFailed': 'Enregistrement impossible',
  'products.toast.deleteFailed': 'Suppression impossible',
  'productForm.newTitle': 'Nouveau produit',
  'productForm.editTitle': 'Modifier le produit',
  'productForm.id': 'Identifiant Izichange',
  'productForm.idPlaceholder': 'crypto',
  'productForm.idHint':
    'L’identifiant envoyé dans « productId » par Izichange. Il est figé après création : c’est lui qui rattache les événements déjà reçus.',
  'productForm.idLocked': 'Non modifiable',
  'productForm.name': 'Nom affiché',
  'productForm.namePlaceholder': 'Crypto Buy/Sell',
  'productForm.description': 'Description',
  'productForm.descriptionPlaceholder': 'À quoi sert ce produit ?',
  'productForm.active': 'Proposé dans les segments',
  'productForm.activeHint':
    'Un produit archivé ne se propose plus ; les segments qui le citent continuent de fonctionner.',
  'product.stat.subscribers': 'Clients liés',
  'product.stat.tx': 'Transactions réussies',
  'product.stat.txFailed': 'Transactions échouées',
  'product.stat.volume': 'Volume USD',
  'product.campaigns.title': 'Campagnes de ce produit',
  'product.campaigns.text': 'Les campagnes qui font la promotion de ce produit, et leurs résultats sur la période.',
  'product.campaigns.empty': 'Aucune campagne ne fait encore la promotion de ce produit.',
  'editor.product': 'Produit promu',
  'editor.product.placeholder': 'Aucun',
  'editor.product.hint': 'Rattache la campagne à un produit : elle apparaît sur sa fiche et dans ses résultats.',
  'editor.excludeProductUsers': 'Ne pas solliciter ceux qui l’utilisent déjà',
  'editor.excludeProductUsers.hint':
    'Vérifié au lancement : un client qui a adopté le produit depuis le figeage du segment est écarté.',
  'segEditor.products.choose': 'Choisir un ou plusieurs produits',
} as const;

export type CrmMessageKey = keyof typeof fr;

const en: Record<CrmMessageKey, string> = {
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.back': 'Back',
  'common.continue': 'Continue',
  'common.previous': 'Previous',
  'common.actions': 'Actions',
  'common.optional': 'optional',
  'common.loading': 'Loading…',
  'common.moreActions': 'More actions',
  'common.openDetails': 'Open details',

  'nav.segments': 'Segments',
  'nav.campaigns': 'Campaigns',
  'nav.templates': 'Email templates',
  'nav.reports': 'Reports',
  'nav.monitoring': 'Monitoring',

  'clients.one': '{count} customer',
  'clients.other': '{count} customers',
  'runs.one': '{count} run',
  'runs.other': '{count} runs',
  'errors.one': '{count} error',
  'errors.other': '{count} errors',
  'steps.one': '{count} email step',
  'steps.other': '{count} email steps',

  'segment.status.ready': 'Ready',
  'segment.status.freezing': 'Freezing list',
  'segment.status.deleting': 'Deleting…',
  'segment.status.failed': 'Freeze failed',
  'campaign.status.draft': 'Draft',
  'campaign.status.active': 'Active',
  'campaign.status.paused': 'Paused',
  'campaign.status.completed': 'Completed',
  'run.status.resolving': 'Building list',
  'run.status.triggering': 'Sending',
  'run.status.triggered': 'Sent',
  'run.status.failed': 'Failed',

  'op.eq': 'is',
  'op.ne': 'is not',
  'op.in': 'is one of',
  'op.nin': 'is none of',
  'op.gt': 'is greater than',
  'op.gte': 'is at least',
  'op.lt': 'is less than',
  'op.lte': 'is at most',
  'op.exists': 'is set',
  'op.not_exists': 'is not set',
  'op.within_last_days': 'is less than',
  'op.more_than_days_ago': 'is more than',
  'op.has': 'uses',
  'op.has_not': 'does not use',
  'op.has_all': 'uses all of',
  'op.has_any': 'uses any of',
  'op.days': 'days old',
  'activity.op.gt': 'greater than',
  'activity.op.gte': 'at least',
  'activity.op.lt': 'less than',
  'activity.op.lte': 'at most',
  'activity.op.eq': 'equal to',
  'segEditor.activityZero':
    'A customer with no activity in the period counts as 0: "equal to 0" targets inactive customers.',

  'field.country_code': 'Country',
  'field.kyc_status': 'KYC status',
  'field.account_created_at': 'Sign-up date',
  'field.kyc_validated_at': 'KYC approval date',
  'field.first_tx_at': 'First transaction',
  'field.last_tx_at': 'Last transaction',
  'field.last_login_at': 'Last login',
  'field.lifetime_tx': 'Transactions (all time)',
  'field.lifetime_vol_usd': 'Volume in USD (all time)',
  'field.marketing_optin': 'Accepts marketing',
  'field.email': 'Email',
  'field.locale': 'Language',
  'metric.tx': 'Successful transactions',
  'metric.volUsd': 'Volume (USD)',
  'metric.txFailed': 'Failed transactions',

  'event.account.registered': 'Sign-up',
  'event.account.profile_updated': 'Profile updated',
  'event.account.email_updated': 'Email changed',
  'event.account.logged_in': 'Login',
  'event.kyc.validated': 'KYC validated',
  'event.transaction.completed': 'Transaction',
  'event.product.activated': 'Product activated',

  'channel.email': 'Email',
  'channel.push': 'Push',
  'channel.in_app': 'In-app',
  'channel.sms': 'SMS',
  'channel.chat': 'Chat',

  'schedule.mode.immediate': 'Now',
  'schedule.mode.scheduled': 'On a date',
  'schedule.mode.recurring': 'On a schedule',
  'schedule.mode.on_event': 'On an event',
  'schedule.describe.immediate': 'Once, on activation',
  'schedule.describe.scheduled': 'On {date}',
  'schedule.describe.onEvent': 'Every time: {event}',
  'schedule.describe.daily': 'Every day at {time}',
  'schedule.describe.weekly': 'Every {days} at {time}',
  'schedule.describe.monthly': 'On day {day} of every month at {time}',
  'schedule.describe.custom': 'Custom schedule ({cron})',
  'schedule.describe.tz': '{text} ({tz})',
  'schedule.and': 'and',
  'weekday.1': 'Monday',
  'weekday.2': 'Tuesday',
  'weekday.3': 'Wednesday',
  'weekday.4': 'Thursday',
  'weekday.5': 'Friday',
  'weekday.6': 'Saturday',
  'weekday.7': 'Sunday',
  'weekday.short.1': 'Mon',
  'weekday.short.2': 'Tue',
  'weekday.short.3': 'Wed',
  'weekday.short.4': 'Thu',
  'weekday.short.5': 'Fri',
  'weekday.short.6': 'Sat',
  'weekday.short.7': 'Sun',
  'freq.daily': 'Every day',
  'freq.weekly': 'Every week',
  'freq.monthly': 'Every month',
  'freq.custom': 'Custom',
  'tz.Africa/Abidjan': 'West Africa, GMT (Abidjan, Dakar, Lomé, Bamako)',
  'tz.Africa/Lagos': 'West Africa, GMT+1 (Cotonou, Niamey, Lagos)',
  'tz.Africa/Douala': 'Central Africa, GMT+1 (Douala, Libreville)',
  'tz.Africa/Kinshasa': 'Kinshasa, GMT+1',
  'tz.Africa/Lubumbashi': 'Lubumbashi, GMT+2',
  'tz.Europe/Paris': 'Paris',
  'tz.UTC': 'UTC',

  'campaigns.description':
    'Send a workflow to a segment: now, on a date, on a schedule, or when a customer takes an action.',
  'campaigns.new': 'New campaign',
  'campaigns.blank.title': 'No campaigns yet',
  'campaigns.blank.text':
    'A campaign pairs a segment (who), a workflow (what) and a moment (when). It stays a draft until you activate it.',
  'campaigns.col.name': 'Campaign',
  'campaigns.col.schedule': 'Delivery',
  'campaigns.col.status': 'Status',
  'campaigns.col.next': 'Next send',
  'campaigns.col.last': 'Last send',
  'campaigns.action.view': 'View results',
  'campaigns.action.activate': 'Activate',
  'campaigns.action.pause': 'Pause',
  'campaigns.toast.activated': 'Campaign activated',
  'campaigns.toast.paused': 'Campaign paused',
  'campaigns.toast.activateFailed': 'Campaign not activated',
  'campaigns.toast.pauseFailed': 'Could not pause',
  'campaigns.toast.deleted': 'Campaign deleted',
  'campaigns.toast.deleteFailed': 'Campaign not deleted',
  'campaigns.delete.title': 'Delete this campaign?',
  'campaigns.delete.text':
    '"{name}" and its history will no longer be visible. Messages already sent are not affected.',
  'campaigns.pauseToEdit': 'Pause the campaign to edit it.',
  'campaigns.pauseToDelete': 'Pause the campaign to delete it.',
  'campaigns.activate.title': 'Activate this campaign?',
  'campaigns.activate.now': 'Messages start sending right away to the customers in "{segment}".',
  'campaigns.activate.later': 'The campaign will send on its schedule: {when}.',
  'campaigns.activate.confirm': 'Activate',

  'editor.newTitle': 'New campaign',
  'editor.editTitle': 'Edit "{name}"',
  'editor.notFound': 'Campaign not found.',
  'editor.locked': 'This campaign is active. Pause it to edit.',
  'step.audience': 'Audience',
  'step.audience.hint': 'Who receives it',
  'step.message': 'Message',
  'step.message.hint': 'The workflow sent',
  'step.schedule': 'Delivery',
  'step.schedule.hint': 'When to send it',
  'step.review': 'Review',
  'step.review.hint': 'Check before saving',
  'step.progress': 'Step {current} of {total}',
  'editor.name.label': 'Campaign name',
  'editor.name.placeholder': 'Win back 30-day inactive customers',
  'editor.name.hint': 'Only visible in the dashboard.',
  'editor.segment.label': 'Segment',
  'editor.segment.placeholder': 'Choose a segment',
  'editor.segment.create': 'Create a segment',
  'editor.segment.empty': 'No segments yet. Create one to choose your audience.',
  'editor.segment.dynamic': 'List recalculated at every send',
  'editor.segment.frozen': 'List frozen on {date}',
  'editor.segment.today': '{clients} today',
  'editor.segment.counting': 'Counting customers…',
  'editor.segment.freezing': 'Freezing in progress: the campaign can send once the list is ready.',
  'editor.segment.exclusions':
    'Customers who opted out of marketing or deleted their account are left out at send time.',
  'editor.workflow.label': 'Workflow',
  'editor.workflow.placeholder': 'Choose a workflow',
  'editor.workflow.hint': 'Content (push, email…) is set in the workflow.',
  'editor.workflow.empty': 'No workflow in this environment. Create it in Development, then publish it.',
  'editor.workflow.open': 'Open workflow',
  'editor.workflow.missing': 'This workflow no longer exists in this environment ({key}).',
  'editor.payload.title': 'Custom data',
  'editor.payload.hint': 'Values you can use in the workflow, for example {{payload.offer}}.',
  'editor.payload.key': 'Name',
  'editor.payload.value': 'Value',
  'editor.payload.add': 'Add a value',
  'editor.payload.remove': 'Remove this value',
  'editor.schedule.label': 'When to send?',
  'editor.schedule.immediate.hint': 'The campaign sends once, as soon as you activate it.',
  'editor.schedule.at.label': 'Date and time',
  'editor.schedule.at.hint': 'In your computer’s time zone.',
  'editor.schedule.frequency': 'Frequency',
  'editor.schedule.days': 'Days',
  'editor.schedule.dayOfMonth': 'Day of the month',
  'editor.schedule.dayOfMonth.hint': 'From 1 to 28, so the campaign also sends in February.',
  'editor.schedule.time': 'Time',
  'editor.schedule.timezone': 'Time zone',
  'editor.schedule.cron': 'Cron expression',
  'editor.schedule.cron.hint': 'For a schedule that can’t be described otherwise.',
  'editor.schedule.event.label': 'Event',
  'editor.schedule.event.placeholder': 'Choose an event',
  'editor.schedule.event.hint':
    'The customer who takes the action receives the campaign if they are in the segment (events under 24 h old).',
  'editor.review.who': 'Who',
  'editor.review.what': 'What',
  'editor.review.when': 'When',
  'editor.review.data': 'Data',
  'editor.review.noData': 'None',
  'editor.review.safety': 'Nothing is sent until the campaign is activated.',
  'editor.review.saveDraft': 'Save as draft',
  'editor.review.saveActivate': 'Save and activate',
  'editor.review.saveChanges': 'Save changes',
  'editor.review.change': 'Change',
  'editor.footer.hint': 'Saved as a draft: nothing is sent until you activate it.',
  'editor.validation.name': 'Give the campaign a name.',
  'editor.validation.segment': 'Choose a segment.',
  'editor.validation.workflow': 'Choose a workflow.',
  'editor.validation.at': 'Choose a future date.',
  'editor.validation.days': 'Choose at least one day.',
  'editor.validation.cron': 'The expression needs 5 parts, for example "0 9 * * 1".',
  'editor.validation.event': 'Choose an event.',
  'editor.toast.draft': 'Campaign saved as draft',
  'editor.toast.activated': 'Campaign saved and activated',
  'editor.toast.updated': 'Campaign updated',
  'editor.toast.failed': 'Campaign not saved',
  'editor.toast.activateFailed': 'Campaign saved but not activated',
  'editor.unsaved': 'Your changes to this campaign will be lost.',

  'detail.results': 'Results',
  'detail.runs': 'Runs',
  'detail.runs.empty': 'No runs yet. They show up here after the first send.',
  'detail.onEvent': 'Event-triggered sends, last 30 days',
  'detail.row.status': 'Status',
  'detail.row.audience': 'Audience',
  'detail.row.workflow': 'Workflow',
  'detail.row.schedule': 'Delivery',
  'detail.row.next': 'Next send',
  'detail.row.last': 'Last send',
  'detail.row.created': 'Created',
  'detail.error': 'Last error',
  'stat.sent': 'Sent',
  'stat.errors': 'Errors',
  'stat.skipped': 'Skipped',
  'stat.opened': 'Opens',
  'stat.clicked': 'Clicks',
  'stat.audience': 'Targeted',
  'stat.excluded': 'Excluded',
  'stat.openRate': 'Open rate',
  'stat.clickRate': 'Click rate',
  'stat.openHint':
    'Push: notification tapped in the app. Email: open reported by the provider, overstated by some mail apps.',
  'stat.skippedHint': 'Not sent: excluded address (bounce, complaint) or customer preferences.',
  'stat.excludedHint': 'Segment customers left out at send time: marketing opt-out or deleted account.',
  'run.col.date': 'Date',
  'run.col.detail': 'Details',

  'segments.description': 'Customer groups defined by profile and activity.',
  'segments.new': 'New segment',
  'segments.blank.title': 'No segments yet',
  'segments.blank.text':
    'A segment groups customers who match criteria: country, KYC, transaction volume… Campaigns use segments to choose their audience.',
  'segments.col.name': 'Segment',
  'segments.col.type': 'List',
  'segments.col.count': 'Customers',
  'segments.col.status': 'Status',
  'segments.col.created': 'Created',
  'segments.type.dynamic': 'Recalculated at every send',
  'segments.type.frozen': 'Frozen on {date}',
  'segments.count.dynamic': 'Computed at send time',
  'segments.action.retry': 'Retry freezing',
  'segments.toast.retried': 'Freezing restarted',
  'segments.toast.retryFailed': 'Could not restart freezing',
  'segments.toast.deleted': 'Segment deleted',
  'segments.toast.deleteFailed': 'Segment not deleted',
  'segments.delete.title': 'Delete this segment?',
  'segments.delete.text': '"{name}" will be deleted. A segment used by a campaign cannot be deleted.',

  'segEditor.title': 'New segment',
  'segEditor.name.label': 'Segment name',
  'segEditor.name.placeholder': 'Active crypto customers, Côte d’Ivoire',
  'segEditor.description.label': 'Description',
  'segEditor.description.placeholder': 'What is this segment for?',
  'segEditor.criteria.title': 'Criteria',
  'segEditor.criteria.lead': 'Customers who match',
  'segEditor.combinator.and': 'all criteria',
  'segEditor.combinator.or': 'at least one criterion',
  'segEditor.criteria.empty': 'With no criteria, the segment includes every customer.',
  'segEditor.addProfile': 'Profile criterion',
  'segEditor.addActivity': 'Activity criterion',
  'segEditor.activityAll': 'With an activity criterion, all criteria must match.',
  'segEditor.connector.and': 'and',
  'segEditor.connector.or': 'or',
  'segEditor.activity.over': 'over the last',
  'segEditor.activity.days': 'days',
  'segEditor.activity.product': 'product',
  'segEditor.activity.allProducts': 'all',
  'segEditor.value.choose': 'Choose',
  'segEditor.value.yes': 'Yes',
  'segEditor.value.no': 'No',
  'segEditor.value.list': 'Choose one or more values',
  'segEditor.value.listText': 'value1, value2',
  'segEditor.removeCriterion': 'Remove this criterion',
  'segEditor.subgroup': 'Criteria group created via the API, not editable here',
  'segEditor.list.title': 'Customer list',
  'segEditor.list.dynamic.title': 'Recalculated at every send',
  'segEditor.list.dynamic.text': 'New customers who match the criteria are included automatically.',
  'segEditor.list.frozen.title': 'Frozen as of today',
  'segEditor.list.frozen.text': 'The list is captured now and never changes. Handy for a one-off operation.',
  'segEditor.preview.title': 'Matching customers',
  'segEditor.preview.hint': 'Computed on up-to-date profiles, before send-time exclusions.',
  'segEditor.preview.incomplete': 'Complete the criteria to see the customer count.',
  'segEditor.create': 'Create segment',
  'segEditor.toast.created': 'Segment created',
  'segEditor.toast.createdFrozen': 'Segment created: the list is being frozen',
  'segEditor.toast.failed': 'Segment not created',
  'segEditor.validation.name': 'Give the segment a name.',
  'segEditor.unsaved': 'The segment is not created yet: your criteria will be lost.',

  'templates.description': 'Reusable emails. Pick one in an email step: every change to the template is copied there.',
  'templates.new': 'New template',
  'templates.blank.title': 'No templates yet',
  'templates.blank.text':
    'Design your emails once in the visual editor, then reuse them in your workflows. Create them in the environment where you edit your workflows.',
  'templates.col.name': 'Template',
  'templates.col.subject': 'Subject',
  'templates.col.version': 'Version',
  'templates.col.updated': 'Updated',
  'templates.toast.deleted': 'Template deleted',
  'templates.toast.deleteFailed': 'Template not deleted',
  'templates.delete.title': 'Delete this template?',
  'templates.delete.text': '"{name}" will be deleted. A template used by an email step cannot be deleted.',
  'templateEditor.newTitle': 'New template',
  'templateEditor.name': 'Name',
  'templateEditor.namePlaceholder': 'KYC reminder',
  'templateEditor.subject': 'Subject suggested to email steps',
  'templateEditor.subjectPlaceholder': '{{subscriber.firstName}}, your KYC is waiting',
  'templateEditor.usedBy': 'Used by {steps}: they will be updated when you save.',
  'templateEditor.toast.saved': 'Template saved',
  'templateEditor.toast.propagated': 'Template saved and copied to {steps}',
  'templateEditor.toast.failed': 'Template not saved',
  'templateEditor.renderFailed': 'Could not render HTML',
  'mergeTag.group.client': 'Customer',
  'mergeTag.group.marketing': 'Marketing',
  'mergeTag.group.campaign': 'Campaign',
  'mergeTag.firstName': 'First name',
  'mergeTag.lastName': 'Last name',
  'mergeTag.email': 'Email',
  'mergeTag.country': 'Country',
  'mergeTag.unsubscribe': 'Unsubscribe link',
  'mergeTag.campaignData': 'Campaign data',
  'templateSelect.placeholder': 'CRM template',
  'templateSelect.none': 'No CRM template',
  'templateSelect.failed': 'Template not applied',

  'reports.description': 'Campaign results for the period: scheduled and event-triggered sends.',
  'reports.period': '{days} d',
  'reports.period.label': 'Period',
  'reports.kpi.campaigns': 'Campaigns',
  'reports.kpi.audience': 'Customers targeted',
  'reports.kpi.audienceHint': 'by scheduled sends',
  'reports.kpi.sent': 'Messages sent',
  'reports.byChannel': 'By channel',
  'reports.byCampaign': 'By campaign',
  'reports.col.channel': 'Channel',
  'reports.col.type': 'Type',
  'reports.col.runs': 'Runs',
  'reports.blank.title': 'No sends in this period',
  'reports.blank.text': 'Results show up here after a campaign’s first send.',
  'reports.truncated': 'More than 1,000 runs: only the most recent are counted.',

  'monitoring.title': 'Monitoring',
  'monitoring.tab.ingestion': 'Event intake',
  'monitoring.tab.email': 'Email sending',
  'monitoring.service.title': 'Intake service',
  'monitoring.service.never': 'The intake service has never reported its status. Check that it is running.',
  'monitoring.service.online': 'Online',
  'monitoring.service.stale': 'No recent signal',
  'monitoring.rabbit.connected': 'Izichange queue connected',
  'monitoring.rabbit.disconnected': 'Izichange queue disconnected',
  'monitoring.service.meta': 'Last signal {date} · started {started} · version {version}',
  'monitoring.stat.waiting': 'Events waiting',
  'monitoring.stat.rejected': 'Rejected events',
  'monitoring.stat.rejectedHint': 'to review',
  'monitoring.stat.lastEvent': 'Last event received',
  'monitoring.stat.profiles': 'Profile updates',
  'monitoring.stat.campaigns': 'Campaign jobs',
  'monitoring.queue': '{waiting} waiting · {active} running · {failed} failed',
  'monitoring.events.title': 'Events received from Izichange, last 14 days',
  'monitoring.events.empty': 'No events received in this period.',
  'monitoring.col.event': 'Event',
  'outcome.accepted': 'Accepted',
  'outcome.duplicate': 'Duplicates',
  'outcome.invalid': 'Rejected',
  'outcome.ignored': 'Ignored',
  'monitoring.rejected.title': 'Rejected events',
  'monitoring.rejected.text':
    'Invalid format or data, kept for 30 days. Once the cause is fixed, send them back to the queue.',
  'monitoring.rejected.count': 'Number to resend',
  'monitoring.rejected.replay': 'Resend',
  'monitoring.rejected.empty': 'No rejected events in the last 30 days.',
  'monitoring.col.received': 'Received',
  'monitoring.col.reason': 'Reason',
  'monitoring.col.content': 'Content',
  'monitoring.replay.toast': 'Resend requested: it is processed within 30 seconds',
  'monitoring.replay.failed': 'Resend not requested',
  'monitoring.commands.title': 'Recent resends',
  'monitoring.command.line': '{date}, up to {limit} events',
  'command.status.pending': 'Pending',
  'command.status.running': 'Running',
  'command.status.done': 'Done',
  'command.status.failed': 'Failed',

  'email.providers.title': 'Campaign providers',
  'email.providers.text':
    'Campaign emails are spread across the providers enabled for campaigns, in priority order. Limits are set in each email integration.',
  'email.providers.empty': 'No provider is used for campaigns: Novu’s primary provider sends them.',
  'email.providers.configure': 'Configure',
  'email.col.provider': 'Provider',
  'email.col.priority': 'Priority',
  'email.col.minute': 'This minute',
  'email.col.hour': 'This hour',
  'email.col.day': 'Today',
  'email.usage.title': 'Sends in the last 14 days',
  'email.usage.empty': 'No sends in this period.',
  'email.usage.sent': 'Sent',
  'email.usage.failed': 'Failed',
  'email.usage.bounced': 'Bounces',
  'email.usage.complaints': 'Complaints',
  'email.usage.postponed': 'Postponed',
  'email.usage.postponedHint': 'Sends put on hold because every provider was at its limit.',
  'email.usage.routing': 'Routing (all full)',
  'sending.title': 'Campaign sending',
  'sending.enabled': 'Use for campaigns',
  'sending.enabled.hint':
    'Campaign emails are spread across the providers enabled here, in priority order. Transactional emails are not affected.',
  'sending.order': 'Priority',
  'sending.order.hint': '1 = tried first.',
  'sending.limits': 'Sending limits',
  'sending.limits.hint':
    'Empty = no limit. When a limit is reached, the next provider takes over; if all are full, sending waits.',
  'sending.perMinute': 'Per minute',
  'sending.perHour': 'Per hour',
  'sending.perDay': 'Per day',
  'sending.noLimit': 'Unlimited',
  'sending.current': 'Right now: {minute} this minute · {hour} this hour · {day} today',
  'sending.save': 'Save sending settings',
  'sending.toast.saved': 'Sending settings saved (applied within 30 seconds)',
  'sending.toast.failed': 'Sending settings not saved',
  'sending.createHint': 'Saved together with the integration.',

  'team.title': 'Team members',
  'team.description':
    'Every member can access the whole "{org}" organization, in Development and Production. This edition has no roles: all members have the same rights.',
  'team.invite.label': 'Invite a colleague',
  'team.invite.placeholder': 'first.last@izichange.com',
  'team.invite.submit': 'Create invitation',
  'team.invite.done': 'Invitation created for {email}. Send them this link:',
  'team.invite.linkHint': 'No email is sent automatically. The link works once, and only for this address.',
  'team.copy': 'Copy link',
  'team.copied': 'Link copied',
  'team.col.member': 'Member',
  'team.col.status': 'Status',
  'team.col.since': 'Since',
  'team.status.active': 'Active',
  'team.status.invited': 'Invitation pending',
  'team.you': 'you',
  'team.empty': 'You are alone in this team for now: invite your colleagues above.',
  'team.action.newLink': 'Generate a new link',
  'team.action.cancelInvite': 'Cancel invitation',
  'team.action.remove': 'Remove from team',
  'team.remove.title': 'Remove {name}?',
  'team.remove.text': 'This person will lose access to "{org}". Their account is not deleted.',
  'team.cancel.title': 'Cancel this invitation?',
  'team.cancel.text': 'The link sent to {email} will stop working.',
  'team.toast.removed': 'Member removed',
  'team.toast.cancelled': 'Invitation cancelled',
  'team.toast.newLink': 'New link generated: the old one no longer works',
  'team.toast.failed': 'Action failed',
  'invite.pageTitle': 'Join a team',
  'invite.loading': 'Checking the invitation…',
  'invite.invalid.title': 'Invalid invitation',
  'invite.invalid.text':
    'This link has already been used or was replaced. Ask the person who invited you for a new link.',
  'invite.goToSignIn': 'Go to sign in',
  'invite.title': 'Join "{org}"',
  'invite.text': '{inviter} invites you to join the "{org}" team on izipush.',
  'invite.signedIn': 'You are already signed in: join the team with this account.',
  'invite.join': 'Join the team',
  'invite.tab.register': 'Create my account',
  'invite.tab.login': 'I already have an account',
  'invite.firstName': 'First name',
  'invite.lastName': 'Last name',
  'invite.firstNameRequired': 'Enter your first name.',
  'invite.email': 'Email',
  'invite.emailHint': 'The invitation is tied to this address.',
  'invite.password': 'Password',
  'invite.passwordHint':
    'At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character (#?!@$%^&*()-).',
  'invite.passwordInvalid': 'The password does not meet the rules below.',
  'invite.submitRegister': 'Create my account and join',
  'invite.submitLogin': 'Sign in and join',
  'org.switch': 'Switch organization',
  'org.switchFailed': 'Could not switch organization',

  'nav.team': 'Team',
  'templateNew.title': 'New template',
  'templateNew.intro':
    'Give your email a name and a description. You will then design it in the full-screen visual editor.',
  'templateNew.step': 'Step {current} of 2',
  'templateNew.step1': 'Details',
  'templateNew.step2': 'Design',
  'templateNew.descriptionPlaceholder': 'What is this email for? Which campaign?',
  'templateNew.subjectHint': 'Suggested to the email steps that use this template. You can change it later.',
  'templateNew.continue': 'Continue to the editor',
  'templateNew.nameRequired': 'Give the template a name.',
  'templateNew.toast.failed': 'Template not created',
  'templateEditor.showMenu': 'Show menu',
  'templateEditor.hideMenu': 'Hide menu',
  'templateEditor.details': 'Details',
  'templateEditor.detailsTitle': 'Template details',
  'templateEditor.detailsText': 'Name and description help you find the template; they are never sent.',
  'templateEditor.toast.detailsSaved': 'Details saved',
  'templateEditor.notFound': 'Template not found.',
  'templateSelect.empty': 'This template has no content yet: design it in the editor first.',

  'funnel.title': 'Message journey',
  'funnel.caption': 'Each percentage is relative to the previous step.',
  'funnel.targeted': 'Targeted',
  'funnel.sent': 'Sent',
  'funnel.opened': 'Opened',
  'funnel.clicked': 'Clicked',
  'funnel.ofPrevious': 'Share of {step}',
  'funnel.excluded': 'Excluded at launch',
  'funnel.skipped': 'Skipped',
  'trend.title': 'Trend by run',
  'trend.aside': 'Last {count} runs',
  'trend.empty': 'The trend appears once two runs have been sent.',
  'detail.runs.open': 'See recipients of the {date} run',
  'detail.onEventLink': 'See messages from the last {days} days',
  'runPage.title': 'Run of {date}',
  'runPage.eventsTitle': 'Event-triggered messages',
  'runPage.eventsDescription': 'Messages sent over the last {days} days.',
  'runPage.channels': 'Sent by channel',
  'runPage.recipients': 'Recipients',
  'runPage.recipientsHint': 'One row per message: a customer gets one message per workflow channel.',
  'runPage.filter.all': 'All',
  'runPage.filter.sent': 'Sent',
  'runPage.filter.unopened': 'Not opened',
  'runPage.filter.opened': 'Opened',
  'runPage.filter.clicked': 'Clicked',
  'runPage.filter.error': 'Errors',
  'runPage.filter.skipped': 'Skipped',
  'runPage.search': 'Exact customer ID or email',
  'runPage.col.client': 'Customer',
  'runPage.col.channel': 'Channel',
  'runPage.col.status': 'Status',
  'runPage.col.sentAt': 'Sent at',
  'runPage.col.openedAt': 'Opened',
  'runPage.col.clickedAt': 'Clicked at',
  'runPage.status.sent': 'Sent',
  'runPage.status.error': 'Error',
  'runPage.status.skipped': 'Skipped',
  'runPage.seen': 'Seen',
  'runPage.read': 'Read',
  'runPage.deletedClient': 'Deleted customer',
  'runPage.empty': 'No message matches this filter.',
  'runPage.emptySearch': 'No message for this customer. Search matches the exact ID or exact email.',
  'runPage.notTriggered': 'This run has not been sent yet.',
  'runPage.loadFailed': 'Could not load recipients. Try again in a moment.',
  'runPage.export': 'Export as CSV',
  'runPage.exporting': 'Exporting… {count}',
  'runPage.exportDone': 'Export ready: {count} rows',
  'runPage.exportCapped': 'Export limited to the first {count} rows',
  'runPage.exportFailed': 'Export failed',

  // izipush-crm — product catalogue
  'nav.products': 'Products',
  'products.description':
    'Izichange products. A customer is linked to a product as soon as they have tried to use it — that link is what makes cross-selling possible.',
  'products.new': 'New product',
  'products.blank.title': 'No product declared',
  'products.blank.text':
    'Declare your products with the identifier Izichange sends in its events. Transactions then attach themselves.',
  'products.col.name': 'Product',
  'products.col.id': 'Identifier',
  'products.col.subscribers': 'Customers',
  'products.col.tx': 'Transactions',
  'products.col.volume': 'USD volume',
  'products.col.status': 'Status',
  'products.status.active': 'Active',
  'products.status.archived': 'Archived',
  'products.status.unnamed': 'Needs a name',
  'products.unnamed.hint': 'Created automatically when an unknown identifier arrived — give it a name.',
  'products.unknown.hint':
    'Bucket for transactions received without a product identifier. Rename it once you know what it covers; it cannot be deleted.',
  'products.archive': 'Archive',
  'products.unarchive': 'Restore',
  'products.delete.title': 'Delete this product?',
  'products.delete.text': 'Product “{name}” will be removed from the catalogue. Its activity and campaigns prevent it.',
  'products.toast.created': 'Product created',
  'products.toast.updated': 'Product saved',
  'products.toast.deleted': 'Product deleted',
  'products.toast.createFailed': 'Could not create',
  'products.toast.updateFailed': 'Could not save',
  'products.toast.deleteFailed': 'Could not delete',
  'productForm.newTitle': 'New product',
  'productForm.editTitle': 'Edit product',
  'productForm.id': 'Izichange identifier',
  'productForm.idPlaceholder': 'crypto',
  'productForm.idHint':
    'The identifier Izichange sends as “productId”. Frozen after creation: it is what attaches already-received events.',
  'productForm.idLocked': 'Cannot be changed',
  'productForm.name': 'Display name',
  'productForm.namePlaceholder': 'Crypto Buy/Sell',
  'productForm.description': 'Description',
  'productForm.descriptionPlaceholder': 'What is this product for?',
  'productForm.active': 'Offered in segments',
  'productForm.activeHint': 'An archived product is no longer offered; segments that reference it keep working.',
  'product.stat.subscribers': 'Linked customers',
  'product.stat.tx': 'Successful transactions',
  'product.stat.txFailed': 'Failed transactions',
  'product.stat.volume': 'USD volume',
  'product.campaigns.title': 'Campaigns for this product',
  'product.campaigns.text': 'Campaigns promoting this product, and their results over the period.',
  'product.campaigns.empty': 'No campaign promotes this product yet.',
  'editor.product': 'Promoted product',
  'editor.product.placeholder': 'None',
  'editor.product.hint': 'Attaches the campaign to a product: it appears on its page and in its results.',
  'editor.excludeProductUsers': 'Skip customers who already use it',
  'editor.excludeProductUsers.hint':
    'Checked at launch: a customer who adopted the product since the segment was frozen is left out.',
  'segEditor.products.choose': 'Choose one or more products',
};

const MESSAGES: Record<CrmLocale, Record<CrmMessageKey, string>> = { fr, en };

type Vars = Record<string, string | number>;

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;

  return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

export function t(key: CrmMessageKey, vars?: Vars): string {
  return interpolate(MESSAGES[crmLocale][key] ?? fr[key], vars);
}

/** Texte au singulier ou au pluriel ; en français, 0 et 1 prennent le singulier. */
export function tp(base: 'clients' | 'runs' | 'errors' | 'steps', count: number, vars?: Vars): string {
  const singular = crmLocale === 'fr' ? count <= 1 : count === 1;
  const key = `${base}.${singular ? 'one' : 'other'}` as CrmMessageKey;

  return t(key, { count: formatNumber(count), ...vars });
}

/** Clé dynamique (champ, événement…) : undefined si elle n'existe pas dans le dictionnaire. */
export function tMaybe(key: string, vars?: Vars): string | undefined {
  return key in fr ? t(key as CrmMessageKey, vars) : undefined;
}

const intlLocale = crmLocale === 'en' ? 'en-GB' : 'fr-FR';

export function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString(intlLocale);
}

export function formatDateTime(value?: string | Date | null): string {
  if (!value) return '—';

  return new Date(value).toLocaleString(intlLocale, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatDay(value?: string | Date | null): string {
  if (!value) return '—';

  return new Date(value).toLocaleDateString(intlLocale, { dateStyle: 'medium' });
}

export function formatList(items: string[]): string {
  if (items.length <= 1) return items.join('');

  return `${items.slice(0, -1).join(', ')} ${t('schedule.and')} ${items[items.length - 1]}`;
}
